import { useEffect, useMemo, useRef } from 'react'
import { useWatch } from 'react-hook-form'
import { captureException } from '@sentry/react'
import { useExponea } from '@/hooks/useExponea'
import { useOneTrust } from '@/hooks/useOneTrust'
import {
  ConvertedObjectIDsAfterSearchEvent,
  useSearchInsights,
} from '@/hooks/useSearchInsights'
import { ProductValues } from '../types'
import { LiquidProduct } from '@/features/product/types'
import { usePostHog } from 'posthog-js/react'

export const useProductTracking = (product?: LiquidProduct) => {
  const searchInsights = useSearchInsights()
  const { hasConsent } = useOneTrust()
  const posthog = usePostHog()
  const exponea = useExponea()
  const viewedTriggered = useRef(false)
  const aaKey = product ? `aa_${product.handle}` : undefined

  const [variantValue] = useWatch<ProductValues, ['variant']>({
    name: ['variant'],
  })

  const [objectID, queryID] = useMemo(() => {
    if (aaKey) {
      const stored = window.sessionStorage.getItem(aaKey)
      if (stored) {
        try {
          return JSON.parse(stored)
        } catch (error) {
          captureException(error)
        }
      }

      return []
    }
  }, [])

  const posthogProductPayload = useMemo(() => {
    if (product) {
      return {
        product_id: product?.id,
        handle: product?.handle,
        title: product?.title,
        price: product?.price,
        compare_at_price: product?.compare_at_price,
        brand: 'Lounge',
        type: product?.type,
        collections: product?.collections,
        product_url: product?.url,
        image_url: product?.images?.[0]?.src,
        sku: variantValue?.sku,
        variant: variantValue?.title,
        available: variantValue?.in_stock,
        stock_level: variantValue?.inventory_quantity,
      }
    }
  }, [product, variantValue])

  useEffect(() => {
    if (viewedTriggered.current) return

    if (objectID) {
      searchInsights.viewedProduct({ objectID })
    }

    if (hasConsent('performance') && product) {
      exponea.track('view_item', {
        product_id: product.id,
        title: `${product.title} - ${product.color}`,
        price: (product.price / 100).toFixed(2),
        brand: 'Lounge',
        _vendor: 'Lounge',
        _collection_title: product.collection,
        variant_id: variantValue?.id,
        tags: product.tags,
        _available: variantValue?.in_stock,
        stock_level: variantValue?.inventory_quantity,
      })
    }

    if (product) {
      posthog?.capture('Product Viewed', posthogProductPayload)
    }

    viewedTriggered.current = true

    return () => {
      if (aaKey) {
        window.sessionStorage.removeItem(aaKey)
      }
    }
  }, [])

  const onAddToCart = () => {
    if (objectID && queryID) {
      searchInsights.convertedObjectIDsAfterSearch({
        objectID,
        queryID,
        eventName: ConvertedObjectIDsAfterSearchEvent.ProductPage,
      })
    }
  }

  return { onAddToCart }
}
